/* @flow */
/**
 * External dependencies
 */
import { connect } from 'react-redux';
// $FlowIssue
import Player from 'react-player/lib/players/FilePlayer';
import React, { Component } from 'react';
import leftPad from 'left-pad';

/**
 * Internal dependencies
 */
import { getWindowWidth, getWindowHeight } from '../../utils/window';
import { play, reset, loaded } from '../../actions/video';
import Icon from '../../components/icon';
import ScreenReaderText from '../../components/screen-reader-text';

/**
 * Style
 */
import {
	FullScreenPlayer,
	PauseScreen,
	PlayButton,
	Progress,
	Time,
	Dot,
	OverLay,
} from './style';

/**
 * Flow
 */
import type { Dispatch } from 'redux';

type VideoProps = {
	dispatch: Dispatch<*>,
	isMainWindow: boolean,
	nextTask: () => void,
	playing: boolean,
	playing: boolean,
	poster: string,
	started: boolean,
	url: string,
};

type VideoState = {
	height: null | number | 'auto',
	played: number,
	playedSeconds: number,
	width: null | number | 'auto',
	length: null | number,
	visible: boolean,
	seeking: boolean,
};

const VIDEO_RATIO = 1.7777777778; // 960x540px

class Video extends Component<VideoProps, VideoState> {
	timelineTimer: any;
	player: any;

	constructor( props ) {
		super( props );

		this.timelineTimer = null;

		this.state = {
			height: null,
			length: null,
			played: 0,
			playedSeconds: 0,
			visible: false,
			width: null,
			seeking: false,
		};
	}

	componentDidMount() {
		window.addEventListener( 'resize', this.handleResize );
		this.handleResize();
		this.props.dispatch( loaded( true ) );
	}

	componentWillUnmount() {
		this.props.dispatch( reset() );
		this.props.dispatch( loaded( false ) );
		window.removeEventListener( 'resize', this.handleResize );
	}

	componentDidUpdate( prevProps ) {
		if ( this.props.url !== prevProps.url ) {
			this.reset();
		}
	}

	reset() {
		this.props.dispatch( reset( false ) );
		this.setState( {
			length: null,
			played: 0,
			playedSeconds: 0,
			visible: false,
		} );
	}

	handleResize = () => {
		this.setState( {
			height: this.getHeight(),
			width: this.getWidth(),
		} );
	}

	getWindowRatio() {
		return getWindowHeight() / getWindowWidth();
	}

	getHeight() {
		return this.getWindowRatio() <= VIDEO_RATIO ? 'auto' : getWindowHeight();
	}

	getWidth() {
		return this.getWindowRatio() > VIDEO_RATIO ? 'auto' : getWindowWidth();
	}

	play = () => {
		this.props.dispatch( play( ! this.props.playing ) );
	}

	onEnded = () => {
		this.props.nextTask();
	}

	onProgress = ( { played, playedSeconds } : { played: number, playedSeconds: number } ) => {
		if ( ! this.state.seeking ) {
			this.setState( { played, playedSeconds } );
		}
	}

	onDuration = ( length: number ) => {
		this.setState( { length } );
	}

	getTime( length ) {
		if ( ! length ) {
			return null;
		}

		const minutes = Math.floor( length / 60 );
		const seconds = Math.ceil( length - ( minutes * 60 ) );

		return `${ leftPad( String( minutes ), 2, '0' ) }:${ leftPad( String( seconds ), 2, '0' ) }`;
	}

	hideTimeline = () => {
		this.setState( { visible: false } );
		clearInterval( this.timelineTimer );
	}

	handleClick = () => {
		if ( this.state.visible ) {
			this.hideTimeline();
		} else {
			this.setState( { visible: ! this.state.visible } );
			this.timelineTimer = setInterval( this.hideTimeline, 3000 );
		}
	}

	onSeekMouseDown = () => {
		clearInterval( this.timelineTimer );
		this.setState( { seeking: true } );
	}

	onSeekChange = event => {
		const length = this.state.length || 1;

		this.setState( {
			played: parseFloat( event.target.value ),
			playedSeconds: Math.ceil( length * event.target.value ),
		} );
	}

	onSeekMouseUp = event => {
		this.setState( { seeking: false } );
		this.player.seekTo( parseFloat( event.target.value ) );
		this.timelineTimer = setInterval( this.hideTimeline, 3000 );
	}

	ref = player => {
		this.player = player;
	}

	render() {
		const {
			height,
			played,
			width,
		} = this.state;

		const {
			isMainWindow,
			playing,
			started,
			url,
		} = this.props;

		return (
			<div>
				<FullScreenPlayer isMainWindow={ isMainWindow }>
					<Player
						ref={ this.ref }
						key={ url }
						className="video-frame"
						config={ { file: { attributes: {
							controlsList: 'nodownload',
							disableremoteplayback: '',
						} } } }
						height={ height }
						onEnded={ this.onEnded }
						onProgress={ this.onProgress }
						onDuration={ this.onDuration }
						playing={ playing }
						playsinline
						url={ url }
						width={ width }
					/>

					{ playing && (
						<Progress visible={ this.state.visible }>
							<button className="progress-pause" type="button" onClick={ this.play }>
								<Icon icon="pause" size={ 100 } />
							</button>

							<span className="dot-inner">
								<span className="dot-inner-inner">
									<Dot played={ played }>
										<span className="dot-time">{ this.getTime( this.state.playedSeconds ) }</span>
									</Dot>
								</span>
								<input
									className="dot-range"
									type="range"
									min={ 0 }
									max={ 1 }
									step="any"
									value={ played }
									onMouseDown={ this.onSeekMouseDown }
									onChange={ this.onSeekChange }
									onMouseUp={ this.onSeekMouseUp }
									onTouchStart={ this.onSeekMouseDown }
									onTouchEnd={ this.onSeekMouseUp }
								/>
							</span>

							<Time>{ this.getTime( this.state.length ) }</Time>
						</Progress>
					) }

					<OverLay onClick={ this.handleClick } />
				</FullScreenPlayer>

				{ ! playing && started && (
					<PauseScreen onClick={ this.play }><div>Du tok en pause gitt. Trykk på skjermen for å starte igjen.</div></PauseScreen>
				) }

				{ isMainWindow && ! playing && ! started && (
					<PlayButton onClick={ this.play } type="button">
						<Icon icon="play-circle" size={ 100 } />
						<ScreenReaderText>{ playing ? 'Pause' : 'Spill av' }</ScreenReaderText>
					</PlayButton>
				) }
			</div>
		);
	}
}

function mapStateToProps( state ) : Object {
	return {
		...state.video,
	};
}

export default connect( mapStateToProps )( Video );



// WEBPACK FOOTER //
// ./src/views/video/index.js